
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import TableFilterable from '@/components/reports-v3/components/filterables/TableFilterable.vue';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import { PermissionsGroup } from '@/store/models.def';
import { TableItemFormatter } from '@/components/reports-v3/components/elements/charts/helpers/tableItemFormatter';
import moment from 'moment';
import gdbx from '@/store/modules/gdbx';
import { getTableName } from '@/worker/db/sqlHelpers';

@Component({
	components: {
		TableFilterable,
	},
})
export default class StockItemMovementTable extends FilterWidget {
	public get permissionIds(): PermissionsGroup[] {
		return ['stocks'];
	}

	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
	> = ['dateRange', 'stockItems', 'agents'];

	public tableItems: any[] = [];
	public tableFields: any[] = [];

	public get exportFileName() {
		const formattedDate = [
			moment(this.selectedDateRange[0]).format('DD MMM YY'),
			moment(this.selectedDateRange[1]).format('DD MMM YY'),
		];
		return (
			'Stock Item Movement' + '_' + formattedDate[0] + ' to ' + formattedDate[1]
		);
	}

	public get dateFormatted() {
		return this.selectedDateRange;
	}

	public get expensiveHook() {
		const { selectedDateRange, selectedAgents, selectedStockItems } = this;
		return JSON.stringify([
			selectedDateRange,
			selectedAgents,
			selectedStockItems,
		]);
	}

	public async expensiveCalc() {
		const jobId = this.currentJobId;
		this.generateLoadingText(0.2, 0.5);

		const queryHead = await this._ensureTablesLoaded(
			['stockMovement'] as any[],
		);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.5, 0.8);

		const stockMovementTable = getTableName('stockMovement');
		const dateRange = this.selectedDateRange;

		const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
		const allStockItemsLen = (gdbx.allStockItems && gdbx.allStockItems.length) || 0;
		const stockItemFilter = this.selectedStockItems && this.selectedStockItems.length > 0
			&& this.selectedStockItems.length !== allStockItemsLen
			? ` AND "ITEMCODE" IN (${this.selectedStockItems.map((s: string) => `'${escapeStr(s)}'`).join(', ')})`
			: '';

		const rows = await queryHead.rawQuery(`
			SELECT "ITEMCODE" as item_code,
				MAX("DESCRIPTION") as item_description,
				SUM(TRY_CAST("COSTIN" AS DOUBLE)) as cost_in,
				SUM(TRY_CAST("PRICEOUT" AS DOUBLE)) as price_out,
				SUM(TRY_CAST("QTYIN" AS DOUBLE)) as quantity_in,
				SUM(ABS(TRY_CAST("QTYOUT" AS DOUBLE))) as quantity_out,
				SUM(TRY_CAST("BALANCEQTY" AS DOUBLE)) as net_movement
			FROM ${stockMovementTable}
			WHERE TRY_CAST("POSTDATE" AS BIGINT) >= ${dateRange[0]}
				AND TRY_CAST("POSTDATE" AS BIGINT) <= ${dateRange[1]}
				${stockItemFilter}
			GROUP BY "ITEMCODE"
		`);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.8, 0.95);

		const newArray: any[] = rows.map((r: any) => ({
			item_code: r.item_code || '',
			item_description: r.item_description || '',
			cost_in: Number(r.cost_in) || 0,
			price_out: Number(r.price_out) || 0,
			quantity_in: Number(r.quantity_in) || 0,
			quantity_out: Number(r.quantity_out) || 0,
			net_movement: Number(r.net_movement) || 0,
		}));

		this.tableFields = [
			{ key: 'item_code', sortable: true, stickyColumn: true },
			{ key: 'item_description', sortable: true },
			{
				key: 'cost_in',
				sortable: true,
				formatter: TableItemFormatter.currencySplit,
			},
			{
				key: 'price_out',
				sortable: true,
				formatter: TableItemFormatter.currencySplit,
			},
			{
				key: 'quantity_in',
				sortable: true,
				formatter: TableItemFormatter.basicInteger,
			},
			{
				key: 'quantity_out',
				sortable: true,
				formatter: TableItemFormatter.basicInteger,
			},
			{
				key: 'net_movement',
				sortable: true,
				formatter: TableItemFormatter.basicInteger,
			},
		];

		// Filter out items with all zeros
		this.tableItems = newArray.filter(
			(item) =>
				item.cost_in !== 0 ||
				item.price_out !== 0 ||
				item.quantity_in !== 0 ||
				item.quantity_out !== 0 ||
				item.net_movement !== 0,
		);

		this.saveHistory('tableItems', 'tableFields');
		this.generateLoadingText(1);
	}
}
